// Mapping `event.code` (position physique de la touche, indépendant du layout
// clavier — identique en AZERTY/QWERTY/Mac) → Virtual-Key code Windows.
//
// Utiliser `event.code` plutôt que `event.key` règle le problème de conversion
// Mac↔Windows : une même touche physique produit toujours le même `code` quelle
// que soit la disposition clavier de l'opérateur.

export const CODE_TO_VK: Record<string, number> = {
  // ── Lettres (rangée alpha) ────────────────────────────────────────────────
  KeyA: 0x41,
  KeyB: 0x42,
  KeyC: 0x43,
  KeyD: 0x44,
  KeyE: 0x45,
  KeyF: 0x46,
  KeyG: 0x47,
  KeyH: 0x48,
  KeyI: 0x49,
  KeyJ: 0x4a,
  KeyK: 0x4b,
  KeyL: 0x4c,
  KeyM: 0x4d,
  KeyN: 0x4e,
  KeyO: 0x4f,
  KeyP: 0x50,
  KeyQ: 0x51,
  KeyR: 0x52,
  KeyS: 0x53,
  KeyT: 0x54,
  KeyU: 0x55,
  KeyV: 0x56,
  KeyW: 0x57,
  KeyX: 0x58,
  KeyY: 0x59,
  KeyZ: 0x5a,

  // ── Chiffres (rangée numérique haute) ─────────────────────────────────────
  Digit0: 0x30,
  Digit1: 0x31,
  Digit2: 0x32,
  Digit3: 0x33,
  Digit4: 0x34,
  Digit5: 0x35,
  Digit6: 0x36,
  Digit7: 0x37,
  Digit8: 0x38,
  Digit9: 0x39,

  // ── Pavé numérique ────────────────────────────────────────────────────────
  Numpad0: 0x60,
  Numpad1: 0x61,
  Numpad2: 0x62,
  Numpad3: 0x63,
  Numpad4: 0x64,
  Numpad5: 0x65,
  Numpad6: 0x66,
  Numpad7: 0x67,
  Numpad8: 0x68,
  Numpad9: 0x69,
  NumpadMultiply: 0x6a,
  NumpadAdd: 0x6b,
  NumpadSubtract: 0x6d,
  NumpadDecimal: 0x6e,
  NumpadDivide: 0x6f,
  NumpadEnter: 0x0d,
  NumpadEqual: 0x0c,
  NumLock: 0x90,

  // ── Touches d'édition / navigation ────────────────────────────────────────
  Backspace: 0x08,
  Tab: 0x09,
  Enter: 0x0d,
  Escape: 0x1b,
  Space: 0x20,
  PageUp: 0x21,
  PageDown: 0x22,
  End: 0x23,
  Home: 0x24,
  ArrowLeft: 0x25,
  ArrowUp: 0x26,
  ArrowRight: 0x27,
  ArrowDown: 0x28,
  Insert: 0x2d,
  Delete: 0x2e,
  Pause: 0x13,
  CapsLock: 0x14,
  PrintScreen: 0x2c,
  ScrollLock: 0x91,
  ContextMenu: 0x5d,

  // ── Modificateurs ─────────────────────────────────────────────────────────
  ShiftLeft: 0xa0, // VK_LSHIFT
  ShiftRight: 0xa1, // VK_RSHIFT
  ControlLeft: 0xa2, // VK_LCONTROL
  ControlRight: 0xa3, // VK_RCONTROL
  AltLeft: 0xa4, // VK_LMENU
  AltRight: 0xa5, // VK_RMENU (AltGr)
  MetaLeft: 0x5b, // VK_LWIN  (Cmd gauche sur Mac)
  MetaRight: 0x5c, // VK_RWIN  (Cmd droite sur Mac)
  OSLeft: 0x5b, // alias historique
  OSRight: 0x5c,

  // ── Touches de fonction ───────────────────────────────────────────────────
  F1: 0x70,
  F2: 0x71,
  F3: 0x72,
  F4: 0x73,
  F5: 0x74,
  F6: 0x75,
  F7: 0x76,
  F8: 0x77,
  F9: 0x78,
  F10: 0x79,
  F11: 0x7a,
  F12: 0x7b,
  F13: 0x7c,
  F14: 0x7d,
  F15: 0x7e,
  F16: 0x7f,
  F17: 0x80,
  F18: 0x81,
  F19: 0x82,
  F20: 0x83,

  // ── Ponctuation / OEM (positions physiques) ───────────────────────────────
  Backquote: 0xc0, // VK_OEM_3
  Minus: 0xbd, // VK_OEM_MINUS
  Equal: 0xbb, // VK_OEM_PLUS
  BracketLeft: 0xdb, // VK_OEM_4
  BracketRight: 0xdd, // VK_OEM_6
  Backslash: 0xdc, // VK_OEM_5
  Semicolon: 0xba, // VK_OEM_1
  Quote: 0xde, // VK_OEM_7
  Comma: 0xbc, // VK_OEM_COMMA
  Period: 0xbe, // VK_OEM_PERIOD
  Slash: 0xbf, // VK_OEM_2
  IntlBackslash: 0xe2, // VK_OEM_102 (touche < > près du Shift gauche, claviers ISO)
  IntlRo: 0xc1, // VK_ABNT_C1
  IntlYen: 0xdc,
};

// VK Windows usuels — utilisés pour les séquences de touches spéciales.
export const VK = {
  BACK: 0x08,
  TAB: 0x09,
  ENTER: 0x0d,
  Q: 0x51,
  SHIFT: 0x10,
  CONTROL: 0x11,
  ALT: 0x12,
  ESCAPE: 0x1b,
  SPACE: 0x20,
  DELETE: 0x2e,
  PRINTSCREEN: 0x2c,
  LWIN: 0x5b,
  RWIN: 0x5c,
  D: 0x44,
  E: 0x45,
  L: 0x4c,
} as const;

/** Convertit un `event.code` en VK Windows, ou `null` si inconnu. */
export function codeToVk(code: string): number | null {
  return CODE_TO_VK[code] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scancodes PS/2 Set 1 (KeyboardEvent.code → scancode RDP).
//
// Indépendant du layout (AZERTY/QWERTY/Mac) : `event.code` désigne la position
// physique de la touche, comme pour `CODE_TO_VK`. Les deux tables coexistent
// car RDP (IronRDP WASM) attend des scancodes PS/2 Set 1 (avec préfixe 0xe0
// pour les touches étendues), tandis que le panel vidéo injecte des VK Windows
// via l'agent C#. Ce sont deux systèmes de numérotation incompatibles.
//
// Reprise de l'exemple officiel `ironrdp-wasm/example/lib/input.js`.
// ─────────────────────────────────────────────────────────────────────────────

export const CODE_TO_SCANCODE: Record<string, number> = {
  Escape: 0x01,
  Digit1: 0x02,
  Digit2: 0x03,
  Digit3: 0x04,
  Digit4: 0x05,
  Digit5: 0x06,
  Digit6: 0x07,
  Digit7: 0x08,
  Digit8: 0x09,
  Digit9: 0x0a,
  Digit0: 0x0b,
  Minus: 0x0c,
  Equal: 0x0d,
  Backspace: 0x0e,
  Tab: 0x0f,
  KeyQ: 0x10,
  KeyW: 0x11,
  KeyE: 0x12,
  KeyR: 0x13,
  KeyT: 0x14,
  KeyY: 0x15,
  KeyU: 0x16,
  KeyI: 0x17,
  KeyO: 0x18,
  KeyP: 0x19,
  BracketLeft: 0x1a,
  BracketRight: 0x1b,
  Enter: 0x1c,
  ControlLeft: 0x1d,
  KeyA: 0x1e,
  KeyS: 0x1f,
  KeyD: 0x20,
  KeyF: 0x21,
  KeyG: 0x22,
  KeyH: 0x23,
  KeyJ: 0x24,
  KeyK: 0x25,
  KeyL: 0x26,
  Semicolon: 0x27,
  Quote: 0x28,
  Backquote: 0x29,
  ShiftLeft: 0x2a,
  Backslash: 0x2b,
  KeyZ: 0x2c,
  KeyX: 0x2d,
  KeyC: 0x2e,
  KeyV: 0x2f,
  KeyB: 0x30,
  KeyN: 0x31,
  KeyM: 0x32,
  Comma: 0x33,
  Period: 0x34,
  Slash: 0x35,
  ShiftRight: 0x36,
  NumpadMultiply: 0x37,
  AltLeft: 0x38,
  Space: 0x39,
  CapsLock: 0x3a,
  F1: 0x3b,
  F2: 0x3c,
  F3: 0x3d,
  F4: 0x3e,
  F5: 0x3f,
  F6: 0x40,
  F7: 0x41,
  F8: 0x42,
  F9: 0x43,
  F10: 0x44,
  NumLock: 0x45,
  ScrollLock: 0x46,
  Numpad7: 0x47,
  Numpad8: 0x48,
  Numpad9: 0x49,
  NumpadSubtract: 0x4a,
  Numpad4: 0x4b,
  Numpad5: 0x4c,
  Numpad6: 0x4d,
  NumpadAdd: 0x4e,
  Numpad1: 0x4f,
  Numpad2: 0x50,
  Numpad3: 0x51,
  Numpad0: 0x52,
  NumpadDecimal: 0x53,
  F11: 0x57,
  F12: 0x58,
  NumpadEnter: 0xe01c,
  ControlRight: 0xe01d,
  NumpadDivide: 0xe035,
  PrintScreen: 0xe037,
  AltRight: 0xe038,
  Home: 0xe047,
  ArrowUp: 0xe048,
  PageUp: 0xe049,
  ArrowLeft: 0xe04b,
  ArrowRight: 0xe04d,
  End: 0xe04f,
  ArrowDown: 0xe050,
  PageDown: 0xe051,
  Insert: 0xe052,
  Delete: 0xe053,
  MetaLeft: 0xe05b,
  MetaRight: 0xe05c,
  ContextMenu: 0xe05d,
  Pause: 0xe11d45,
};

// Scancodes PS/2 usuels — équivalent de `VK` pour le panel RDP. Les codes
// étendus (préfixe 0xe0) sont nécessaires pour Ctrl droit, Cmd, flèches, etc.
export const SC = {
  ESCAPE: 0x01,
  ENTER: 0x1c,
  CTRL: 0x1d, // ControlLeft
  CTRL_RIGHT: 0xe01d, // ControlRight
  SHIFT: 0x2a,
  SHIFT_RIGHT: 0x36,
  ALT: 0x38,
  V: 0x2f,
  C: 0x2e,
  X: 0x2d,
  A: 0x1e,
  SPACE: 0x39,
  CAPSLOCK: 0x3a,
  NUMLOCK: 0x45,
  SCROLLLOCK: 0x46,
  DELETE: 0xe053,
  BACKSPACE: 0x0e,
  TAB: 0x0f,
  WIN_LEFT: 0xe05b, // MetaLeft (touche Windows / Cmd gauche sur Mac)
  WIN_RIGHT: 0xe05c, // MetaRight
} as const;

/** Convertit un `event.code` en scancode PS/2 Set 1, ou `null` si inconnu. */
export function codeToScancode(code: string): number | null {
  return CODE_TO_SCANCODE[code] ?? null;
}

/** Détecte si l'opérateur utilise un Mac (pour le swap Cmd↔Ctrl par défaut). */
export function isMacPlatform(): boolean {
  try {
    const uaPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
    if (uaPlatform) return /mac/i.test(uaPlatform);
  } catch {
    /* ignore */
  }
  return /mac/i.test(navigator.platform || navigator.userAgent || "");
}
